import { useEffect } from 'react';

type FAQ = {
  question: string;
  answer: string;
};

const SITE_URL = 'https://personalfinancecare.net';
const SITE_NAME = 'Personal Finance Care';
const OG_IMAGE = `${SITE_URL}/logo512.png`;

const homeSeo = {
  title: 'Subscription Management| PersonalFinanceCare',
  description:
    'PersonalFinanceCare helps you manage subscriptions with ease. Get expert after-sales support and fast help from partner-trained agents.',
  url: `${SITE_URL}/`,
  canonical: `${SITE_URL}/`,
  ogTitle: 'Personal Finance Care | Smart Financial Guidance & Money Management',
  ogDescription:
    'Discover practical strategies for budgeting, saving, investing, debt management, and building long-term financial confidence.',
  ogImage: OG_IMAGE,
};

export const faqItems: FAQ[] = [
  {
    question: 'Why do I have a personalfinancecare.net charge on my bank statement?',
    answer:
      'If a direct debit appears on your statement, you have probably signed up to a subscription to one of our partner sites for which we provide the after-sales service. Please note that you are not a client of personalfinancecare.net, but a client of a site that has delegated the management of its customer service.',
  },
  {
    question: 'What are the services offered by personalfinancecare.net?',
    answer:
      'personalfinancecare.net offers simplified management of your subscription. Fill in the form on the home page with an e-mail address used at the time of subscription. You will be able to contact the agents responsible for your file, obtain all the information concerning your subscription or cancel it.',
  },
  {
    question: 'What kind of subscription needs after-sales service?',
    answer:
      'personalfinancecare.net provides customer service for subscriptions that make your life easier. We work with sites that offer help to individuals or professionals, accompanying each client through various operations or steps. This guarantees the after-sales service of every partner site.',
  },
  {
    question: 'I do not remember signing up to a personalfinancecare.net subscription.',
    answer:
      'You have not signed up to a personalfinancecare.net subscription. You have signed up to a subscription at one of our partner sites for which we provide after-sales service. That is why the direct debit appeared on your bank statement.',
  },
  {
    question: 'How do I find the offer to which I subscribed?',
    answer:
      'To find the details of the offer to which you subscribed, fill in the form on the home page using the email address used when registering. We will then provide important information about your subscription, including the date and amount of the next withdrawal and the service features selected.',
  },
  {
    question: 'I forgot the email address used when subscribing. What can I do?',
    answer:
      'If you are unable to retrieve the email address used during the subscription, our agents may be able to find your client file. Contact our agents by email or through the contact form and provide any details that can help identify your account.',
  },
  {
    question: 'Who can I contact if I have a question?',
    answer:
      'personalfinancecare.net agents are experts in after-sales service and are always available to help. We work seven days a week, so you can contact the agents whenever you need. You can also send them a message through our contact form.',
  },
  {
    question: 'How can I cancel my subscription?',
    answer:
      'To cancel your subscription, go to your customer account and select the cancellation option. You will no longer benefit from the partner site services and will no longer be charged. A confirmation email will be sent after the cancellation is completed.',
  },
];

const faqSeo = {
  title: 'Frequently Asked Questions | PersonalFinanceCare',
  description:
    'Find answers about subscriptions, billing, account access, and cancellations at Personal Finance Care.',
  url: `${SITE_URL}/faqs.html`,
  canonical: `${SITE_URL}/faqs.html`,
  ogTitle: 'Frequently Asked Questions | Personal Finance Care',
  ogDescription:
    'Find answers to common questions about Personal Finance Care services, programs, billing, account access, and financial education resources.',
  ogImage: OG_IMAGE,
};

const pageSchemas = {
  home: {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: 'Personal Finance Care',
        url: `${SITE_URL}/`,
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
        },
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: 'Personal Finance Care',
        publisher: {
          '@id': `${SITE_URL}/#organization`,
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: `${SITE_URL}/`,
        name: 'Personal Finance Care',
        description:
          'Online customer care portal for managing and cancelling Personal Finance Care subscriptions.',
        isPartOf: {
          '@id': `${SITE_URL}/#website`,
        },
        about: {
          '@id': `${SITE_URL}/#cancellation-service`,
        },
      },
      {
        '@type': 'Service',
        '@id': `${SITE_URL}/#cancellation-service`,
        name: 'Personal Finance Care Subscription Cancellation Service',
        description:
          'Online service for managing and cancelling Personal Finance Care partner subscriptions.',
        url: `${SITE_URL}/`,
        provider: {
          '@id': `${SITE_URL}/#organization`,
        },
      },
    ],
  },
  faq: {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  },
};

function upsertMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element!.setAttribute(key, value);
  });
}

function upsertLink(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLLinkElement>(selector);
  if (!element) {
    element = document.createElement('link');
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element!.setAttribute(key, value);
  });
}

function upsertSchema(schema: object) {
  const id = 'page-schema';
  let element = document.getElementById(id) as HTMLScriptElement | null;
  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(schema);
}

export default function SEO({ page }: { page: 'home' | 'faq' }) {
  useEffect(() => {
    const seo = page === 'faq' ? faqSeo : homeSeo;
    const schema = page === 'faq' ? pageSchemas.faq : pageSchemas.home;

    document.title = seo.title;
    upsertMeta('meta[name="description"]', {
      name: 'description',
      content: seo.description,
    });
    upsertMeta('meta[property="og:type"]', {
      property: 'og:type',
      content: 'website',
    });
    upsertMeta('meta[property="og:site_name"]', {
      property: 'og:site_name',
      content: SITE_NAME,
    });
    upsertMeta('meta[property="og:title"]', {
      property: 'og:title',
      content: seo.ogTitle,
    });
    upsertMeta('meta[property="og:description"]', {
      property: 'og:description',
      content: seo.ogDescription,
    });
    upsertMeta('meta[property="og:url"]', {
      property: 'og:url',
      content: seo.url,
    });
    upsertMeta('meta[property="og:image"]', {
      property: 'og:image',
      content: seo.ogImage,
    });
    upsertMeta('meta[name="robots"]', {
      name: 'robots',
      content: 'index, follow',
    });
    upsertMeta('meta[name="twitter:card"]', {
      name: 'twitter:card',
      content: 'summary',
    });
    upsertMeta('meta[name="twitter:title"]', {
      name: 'twitter:title',
      content: seo.ogTitle,
    });
    upsertMeta('meta[name="twitter:description"]', {
      name: 'twitter:description',
      content: seo.ogDescription,
    });
    upsertMeta('meta[name="twitter:image"]', {
      name: 'twitter:image',
      content: seo.ogImage,
    });
    upsertLink('link[rel="canonical"]', {
      rel: 'canonical',
      href: seo.canonical,
    });
    upsertSchema(schema);
  }, [page]);

  return null;
}
